import { ZxingEngine } from "../engine/ZxingEngine.js";
import { analyzeFrame, enhanceForCondition, lumaToImageData, toLuma, TemporalDenoiser } from "../enhance/index.js";
import type { FrameQuality, ScanResult, ScanSettings } from "../types.js";

export interface NormalizedRoi {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ScanPipelineOptions {
  engine: ZxingEngine;
  video: HTMLVideoElement;
  getSettings: () => ScanSettings;
  getRoi?: () => NormalizedRoi | null;
  /** `frame` = all matches visible this frame (for AR overlay), `fresh` = newly discovered (not seen before), `batch` = all fresh results accumulated since `start()`. */
  onResults?: (frame: ScanResult[], fresh: ScanResult[], batch: ScanResult[]) => void;
  onFrameQuality?: (q: FrameQuality) => void;
  onTorchRequest?: (on: boolean) => void;
  onError?: (err: Error) => void;
  /** Max processed frames per second; keeps the enhancement chain from starving the render thread. */
  targetFps?: number;
  /** Longest edge (px) the analysis canvas is downscaled to before decoding. */
  maxProcessingSize?: number;
}

const DEFAULT_ROI: NormalizedRoi = { x: 0.1, y: 0.28, width: 0.8, height: 0.44 };

export class ScanPipeline {
  private readonly opts: Required<Pick<ScanPipelineOptions, "targetFps" | "maxProcessingSize">> & ScanPipelineOptions;
  private readonly canvas = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D;
  private readonly denoiser = new TemporalDenoiser(3);
  private running = false;
  private busy = false;
  private rafHandle = 0;
  private lastProcessedAt = 0;
  private lowLightStreak = 0;
  private torchOn = false;
  private readonly seen = new Set<string>();
  private readonly batch: ScanResult[] = [];

  constructor(options: ScanPipelineOptions) {
    this.opts = { targetFps: 8, maxProcessingSize: 720, ...options };
    const ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.seen.clear();
    this.batch.length = 0;
    this.denoiser.reset();
    this.lowLightStreak = 0;
    this.loop();
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
  }

  private loop = (): void => {
    if (!this.running) return;
    this.rafHandle = requestAnimationFrame(this.loop);
    const now = performance.now();
    const minInterval = 1000 / this.opts.targetFps;
    if (this.busy || now - this.lastProcessedAt < minInterval) return;
    const { video } = this.opts;
    if (video.readyState < 2 || video.videoWidth === 0) return;
    this.lastProcessedAt = now;
    this.busy = true;
    this.processFrame().finally(() => {
      this.busy = false;
    });
  };

  private captureRoi(): { imageData: ImageData; roiPx: { x: number; y: number; w: number; h: number } } {
    const { video } = this.opts;
    const roi = this.opts.getRoi?.() ?? DEFAULT_ROI;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const roiPx = {
      x: Math.round(roi.x * vw),
      y: Math.round(roi.y * vh),
      w: Math.round(roi.width * vw),
      h: Math.round(roi.height * vh),
    };
    const scale = Math.min(1, this.opts.maxProcessingSize / Math.max(roiPx.w, roiPx.h));
    const outW = Math.max(1, Math.round(roiPx.w * scale));
    const outH = Math.max(1, Math.round(roiPx.h * scale));
    this.canvas.width = outW;
    this.canvas.height = outH;
    this.ctx.drawImage(video, roiPx.x, roiPx.y, roiPx.w, roiPx.h, 0, 0, outW, outH);
    const imageData = this.ctx.getImageData(0, 0, outW, outH);
    return { imageData, roiPx };
  }

  private mapPositionToVideoSpace(
    pos: ScanResult["position"],
    processedSize: { w: number; h: number },
    roiPx: { x: number; y: number; w: number; h: number },
  ): ScanResult["position"] {
    const sx = roiPx.w / processedSize.w;
    const sy = roiPx.h / processedSize.h;
    const map = (p: { x: number; y: number }) => ({ x: roiPx.x + p.x * sx, y: roiPx.y + p.y * sy });
    return {
      topLeft: map(pos.topLeft),
      topRight: map(pos.topRight),
      bottomLeft: map(pos.bottomLeft),
      bottomRight: map(pos.bottomRight),
    };
  }

  private async processFrame(): Promise<void> {
    const settings = this.opts.getSettings();
    if (settings.enabledFormats.length === 0) return;

    let capture: ReturnType<ScanPipeline["captureRoi"]>;
    try {
      capture = this.captureRoi();
    } catch (err) {
      this.opts.onError?.(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    const { imageData, roiPx } = capture;

    const luma = toLuma(imageData);
    const { averaged, stable } = this.denoiser.push(luma);
    const quality = analyzeFrame(averaged, imageData.width, imageData.height);
    this.opts.onFrameQuality?.(quality);
    this.handleAutoTorch(settings, quality);

    try {
      let results = await this.opts.engine.decode(imageData, settings.enabledFormats, { maxNumberOfSymbols: 5 });
      let enhanced = false;

      if (results.length === 0 && settings.enhancement !== "off" && quality.condition !== "good") {
        const source = stable ? averaged : luma;
        const processedLuma = enhanceForCondition(
          source,
          imageData.width,
          imageData.height,
          quality,
          settings.enhancement === "aggressive",
        );
        const enhancedImageData = lumaToImageData(processedLuma, imageData.width, imageData.height);
        // See decodeWithEnhancement.ts: processed pixels can manufacture
        // edge structure, so the retry demands more consistent scan lines.
        results = await this.opts.engine.decode(enhancedImageData, settings.enabledFormats, {
          maxNumberOfSymbols: 5,
          minLineCount: 3,
        });
        enhanced = results.length > 0;
      }

      if (results.length > 0) {
        this.emitResults(results, imageData, roiPx, enhanced);
      } else {
        // A frame with no match still has to be reported (with an empty
        // `frame` array) so the AR overlay gets cleared — otherwise the
        // last box drawn for a since-moved-away code stays stuck on screen
        // forever, since nothing else ever tells the view to erase it.
        this.opts.onResults?.([], [], this.batch);
      }
    } catch (err) {
      this.opts.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private handleAutoTorch(settings: ScanSettings, quality: FrameQuality): void {
    if (settings.torch !== "auto") return;
    if (quality.condition === "low-light") {
      this.lowLightStreak++;
    } else {
      this.lowLightStreak = 0;
    }
    if (!this.torchOn && this.lowLightStreak >= 6) {
      this.torchOn = true;
      this.opts.onTorchRequest?.(true);
    } else if (this.torchOn && (quality.condition === "glare" || quality.condition === "good")) {
      this.torchOn = false;
      this.opts.onTorchRequest?.(false);
    }
  }

  private emitResults(
    raw: Omit<ScanResult, "enhanced" | "timestamp">[],
    imageData: ImageData,
    roiPx: { x: number; y: number; w: number; h: number },
    enhanced: boolean,
  ): void {
    const settings = this.opts.getSettings();
    const timestamp = performance.now();
    const mapped: ScanResult[] = raw
      .filter((r) => settings.enabledFormats.includes(r.format))
      .map((r) => ({
        ...r,
        enhanced,
        timestamp,
        position: this.mapPositionToVideoSpace(r.position, { w: imageData.width, h: imageData.height }, roiPx),
      }));
    if (mapped.length === 0) return;

    const fresh = mapped.filter((r) => !this.seen.has(`${r.format}:${r.text}`));
    if (fresh.length === 0 && !settings.multipleScanning) return;

    for (const r of fresh) {
      this.seen.add(`${r.format}:${r.text}`);
      this.batch.push(r);
    }

    const frame = settings.multipleScanning ? mapped : mapped.slice(0, 1);
    this.opts.onResults?.(frame, settings.multipleScanning ? fresh : frame, this.batch);

    if (!settings.multipleScanning) {
      this.stop();
    }
  }
}
